import { breakpoints, Button, Dialog, Inline, Stack, Text } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useApolloContext } from '@eti/authentication';
import { UPDATE_SESSION } from '../../utils/mutations';

interface SessionWarningDialogProps {
  handleOpenSessionExpiredDialog: (a: boolean) => void;
}

const SessionWarningDialog = ({ handleOpenSessionExpiredDialog }: SessionWarningDialogProps) => {
  const { t } = useTranslation();
  const { lastCallTimestamp } = useApolloContext();
  const [updateSession] = useMutation(UPDATE_SESSION);
  const [isSessionWarningDialogOpen, setIsSessionWarningDialogOpen] = useState(false);
  const secondTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // TODO: replace the sessionTimeoutSecs with a spa_variable
    const sessionTimeoutSecs = 1800;
    if (!sessionTimeoutSecs || !lastCallTimestamp) {
      return () => {};
    }

    const firstTimeOutId = setTimeout(
      () => {
        setIsSessionWarningDialogOpen(true);

        secondTimeoutRef.current = setTimeout(() => {
          setIsSessionWarningDialogOpen(false);
          handleOpenSessionExpiredDialog(true);
        }, 300000);
      },
      sessionTimeoutSecs * 1000 - 300000,
    );

    return () => {
      clearTimeout(firstTimeOutId);
      clearTimeout(secondTimeoutRef.current);
    };
  }, [handleOpenSessionExpiredDialog, setIsSessionWarningDialogOpen, lastCallTimestamp]);

  const keepSessionAlive = async () => {
    await updateSession();
    setIsSessionWarningDialogOpen(false);
    clearTimeout(secondTimeoutRef.current);
  };

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="session-will-expire-dialog"
      isOpen={isSessionWarningDialogOpen}
      title={t('IbeClient.SessionWillExpire.Modal.Title.Text')}
    >
      <Stack>
        <Text>{t('IbeClient.SessionWillExpire.Modal.Content.Text')}</Text>
        <Inline align="end" collapseBelow={breakpoints._560} spacing={8}>
          <Button
            data-testid="leave-session-expire-button"
            onClick={() => setIsSessionWarningDialogOpen(false)}
            variant="secondary"
          >
            {t('IbeClient.SessionWillExpire.Modal.Dismiss.Button.Text')}
          </Button>
          <Button
            data-testid="keep-session-alive-button"
            onClick={keepSessionAlive}
            variant="primary"
          >
            {t('IbeClient.SessionWillExpire.Modal.Update.Button.Text')}
          </Button>
        </Inline>
      </Stack>
    </Dialog>
  );
};

export default SessionWarningDialog;
